import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import SVG from 'react-inlinesvg';
import Pagination from '../../../components/Pagination';
import Table from '../../../components/Table';
import { convertToDollarString, formatDate } from '../../../helpers';

import { adminAPI } from '../../../api';

import EditIcon from '../../../../../../assets/images/reskin-images/icon--edit.svg';
import EyeIcon from '../../../../../../assets/images/reskin-images/icon--eye.svg';
import BinIcon from '../../../../../../assets/images/reskin-images/icon--bin.svg';
import SendIcon from '../../../../../../assets/images/reskin-images/icon--send.svg';
import useModal from '../../../context/ModalContext';
import DestroyModal from './DestroyModal';
import ResendInviteModal from './ResendInviteModal';

const PendingInvitesTable = ({
  searchQuery,
  filter,
  items,
  onPaginationChange,
  pendingInvites,
  setPendingInvites,
  setOpenSideActionBoard,
  checkedAllUsers,
  handleSelectAllUsers,
  totalUsers,
}) => {
  const [currentPage, setCurrentPage] = useState(
    items?.pagination?.current_page || 1
  );

  useEffect(() => {
    onPaginationChange(currentPage);
  }, [currentPage]);

  const columns = [
    {
      label: 'Email',
      field: 'email',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '25%',
    },
    {
      label: 'Domain',
      field: 'domain',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Access Type',
      field: 'access_type',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Sent by',
      field: 'sent_by',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '20%',
    },
    {
      label: 'Sent',
      field: 'sent',
      textAlign: 'center',
      width: '12%',
    },
    {
      label: 'Expires',
      field: 'expires',
      textAlign: 'center',
      width: '13%',
    },
  ];

  const [rowHovered, setRowHovered] = useState(false);

  const reconstructorFn = (userInvite) => {
    const {
      id,
      email,
      domain,
      invited_at: invitedAt,
      expires_at: expiresAt,
      expiration_type: expirationType,
      valid_for_days: validForDays,
      discounted_cents: discountedCents,
      application,
      access_type,
      invited_by_email: invitedByEmail,
      invited_by_name: invitedByName,
    } = userInvite;

    const actionButtons = (
      <div
        className={` absolute pin-r pin-t h-full items-center pr-2 ${
          rowHovered === id ? 'flex' : 'hidden'
        }`}
      >
        <button
          type="button"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg mr-2 hover:bg-purple-200"
          onClick={() =>
            setOpenSideActionBoard({
              type: 'Pending Invites',
              action: 'view',
              ids: [{ id, email }],
            })
          }
        >
          <SVG src={EyeIcon} />
        </button>
        <button
          type="button"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg hover:bg-purple-200"
          onClick={() =>
            setOpenSideActionBoard({
              type: 'Pending Invites',
              action: 'edit',
              ids: [{ id, email }],
            })
          }
        >
          <SVG src={EditIcon} />
        </button>
      </div>
    );

    // For new Fixed Length invites (no expires_at), show "---" since expiration is calculated after signup
    // For old Fixed Length invites (has expires_at), show the date as before
    const expiresDisplay = (expirationType === 'length' && !expiresAt)
      ? '---'
      : (expiresAt ? formatDate(expiresAt) : '---');

    return {
      id,
      email,
      domain,
      application: id ? (application ?? 'ald') : '',
      access_type: access_type ?? '---',
      sent_by: invitedByName || invitedByEmail || '---',
      sent: formatDate(invitedAt),
      expires: expiresDisplay,
      discount: convertToDollarString(discountedCents),
    };
  };

  const { setContent } = useModal();

  return (
    <div>
      <div className="mb-4 ml-10 flex items-center">
        <h2 className="font-semibold">Pending Invites</h2>
        {pendingInvites?.length ? (
          <button
            onClick={() =>
              setContent({
                content: (
                  <ResendInviteModal
                    items={pendingInvites}
                    setItems={setPendingInvites}
                    checkedAllUsers={checkedAllUsers}
                    totalUsers={totalUsers}
                    filter={filter}
                    searchQuery={searchQuery}
                    handleSelectAllUsers={handleSelectAllUsers}
                  />
                ),
              })
            }
            type="button"
            className="flex items-center font-semibold bg-purple-100 rounded-lg py-3 px-4 ml-8 hover:bg-purple-lightest"
          >
            <SVG src={SendIcon} className="mr-2" />
            Resend {checkedAllUsers ? totalUsers.toLocaleString('en-US') : pendingInvites.length} Selected Invites
          </button>
        ) : null}
        {pendingInvites?.length ? (
          <button
            type="button"
            className="flex items-center font-semibold hover:bg-red-lighter rounded-lg p-3 ml-4"
            onClick={() => {
              setContent({
                content: (
                  <DestroyModal
                    items={pendingInvites}
                    type="invites"
                    queryListId="pendingInvites"
                    setItems={setPendingInvites}
                    checkedAllUsers={checkedAllUsers}
                    totalUsers={totalUsers}
                    filter={filter}
                    searchQuery={searchQuery}
                    handleSelectAllUsers={handleSelectAllUsers}
                  />
                ),
              });
            }}
          >
            <SVG src={BinIcon} />
          </button>
        ) : null}
      </div>

      {!items?.isLoading && (
        <Table
          id="pending_invites"
          data={items?.user_invites}
          columns={columns}
          reconstructorFn={reconstructorFn}
          checkedItems={pendingInvites}
          setCheckedItems={setPendingInvites}
          setRowHovered={setRowHovered}
          totalCount={items?.pagination?.total_count}
          onSelectAllRecords={handleSelectAllUsers}
          checkedAllUsers={checkedAllUsers}
        />
      )}
      <Pagination
        currentPage={currentPage}
        totalPage={items?.pagination?.total_pages}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  );
};

export default PendingInvitesTable;
